import * as React from 'react';
import { cva } from 'class-variance-authority';
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Clock,
  FileText,
  User
} from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * ActivityItem - Activity feed item with icon, content, and timestamp
 *
 * Hyperkit Specs:
 * - Icon container: 32px, rounded-full, semantic color background
 * - Title: 14px/500
 * - Detail: 13px, grey-600
 * - Time: 12px, grey-500
 * - Hover state with bg transition
 */

const iconContainerVariants = cva(
  'flex h-8 w-8 items-center justify-center rounded-full shrink-0',
  {
    variants: {
      type: {
        approved: 'bg-green-500/15 text-green-500',
        rejected: 'bg-red-500/15 text-red-500',
        screening_hit: 'bg-orange-500/15 text-orange-500',
        resubmission: 'bg-blue-500/15 text-blue-500',
        document_uploaded: 'bg-purple-500/15 text-purple-500',
        default: 'bg-grey-100 text-grey-600 dark:bg-grey-800 dark:text-grey-400',
      },
    },
    defaultVariants: {
      type: 'default',
    },
  }
);

// Default icons for activity types
const typeIcons = {
  approved: CheckCircle2,
  rejected: XCircle,
  screening_hit: AlertTriangle,
  resubmission: Clock,
  document_uploaded: FileText,
  default: User,
};

const ActivityItem = React.forwardRef(({
  className,
  type = 'default',
  icon: CustomIcon,
  title,
  detail,
  time,
  onClick,
  ...props
}, ref) => {
  const Icon = CustomIcon || typeIcons[type] || typeIcons.default;

  return (
    <div
      ref={ref}
      className={cn(
        'flex items-start gap-3 py-3.5 -mx-3 px-3 rounded-lg',
        'border-b border-grey-200 dark:border-grey-700 last:border-b-0',
        'transition-colors duration-150',
        onClick && 'cursor-pointer hover:bg-grey-50 dark:hover:bg-grey-800/50',
        className
      )}
      onClick={onClick}
      {...props}
    >
      {/* Icon */}
      <div className={iconContainerVariants({ type })}>
        <Icon size={16} />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="text-body-sm font-medium text-grey-900 dark:text-grey-50 truncate">
          {title}
        </div>
        {detail && (
          <div className="text-body-xs text-grey-600 dark:text-grey-400 truncate mt-0.5">
            {detail}
          </div>
        )}
      </div>

      {/* Time */}
      {time && (
        <span className="text-body-xs text-grey-500 dark:text-grey-500 whitespace-nowrap shrink-0">
          {time}
        </span>
      )}
    </div>
  );
});
ActivityItem.displayName = 'ActivityItem';

/**
 * ActivityItemSkeleton - Loading state
 */
const ActivityItemSkeleton = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'flex items-start gap-3 py-3.5 animate-pulse',
      'border-b border-grey-200 dark:border-grey-700 last:border-b-0',
      className
    )}
    {...props}
  >
    <div className="h-8 w-8 rounded-full bg-grey-200 dark:bg-grey-700 shrink-0" />
    <div className="flex-1 space-y-2">
      <div className="h-4 w-28 rounded bg-grey-200 dark:bg-grey-700" />
      <div className="h-3 w-44 rounded bg-grey-200 dark:bg-grey-700" />
    </div>
    <div className="h-3 w-14 rounded bg-grey-200 dark:bg-grey-700" />
  </div>
));
ActivityItemSkeleton.displayName = 'ActivityItemSkeleton';

export { ActivityItem, ActivityItemSkeleton, iconContainerVariants, typeIcons };
